/**
 * MODULE: domain/templates/frontend/api/slideBuilderJobsApi.ts
 * PURPOSE: Job API client — POST/GET /api/v1/slide-decks/generate-jobs
 * DEPENDENCIES: @shared/lib/frontend/api/config
 * PUBLIC_API: slideBuilderJobsApi, SlideBuilderGenerateJob, SlideBuilderGenerateJobRequest
 * SIDE_EFFECTS: HTTP
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: ~110 / 150 lines
 * RECENT: 2026-08-04 [2.2.2047] — request_echo on job view for regenerate.
 */

import { API_CONFIG, getAuthHeaders } from '@shared/lib/frontend/api/config';

const BASE = `${API_CONFIG.baseURL.replace(/\/$/, '')}/api/v1/slide-decks`;

export interface SlideBuilderGenerateJobRequest {
  title?: string;
  brief?: string;
  recipe_id?: string;
  deck_arc?: string;
  deal_stage?: string;
  github_org_url?: string;
  company_name?: string;
  deal_name?: string;
  outline_sections?: { title?: string; summary?: string }[];
  data?: Record<string, unknown>;
}

export interface SlideBuilderGenerateJob {
  id: string;
  status: 'queued' | 'assembling' | 'persisting' | 'completed' | 'failed' | string;
  progress: number;
  message: string;
  deck_id?: string | null;
  recipe_id?: string | null;
  slide_count?: number;
  error?: string | null;
  artifacts?: Record<string, unknown>;
  /** Echo of create body — used by companion regenerate. */
  request_echo?: SlideBuilderGenerateJobRequest;
  created_at: string;
  updated_at: string;
  sales_hub_path?: string | null;
}

async function fetchJob<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error((err as { detail?: string }).detail || `HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export const slideBuilderJobsApi = {
  create: (body: SlideBuilderGenerateJobRequest) =>
    fetchJob<SlideBuilderGenerateJob>('/generate-jobs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  get: (jobId: string) => fetchJob<SlideBuilderGenerateJob>(`/generate-jobs/${encodeURIComponent(jobId)}`),

  /**
   * Poll until completed/failed. Resolves with the final job (deck_id set on success).
   */
  async waitForDeck(
    jobId: string,
    options?: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal },
  ): Promise<SlideBuilderGenerateJob> {
    const intervalMs = options?.intervalMs ?? 400;
    const timeoutMs = options?.timeoutMs ?? 60_000;
    const started = Date.now();
    for (;;) {
      if (options?.signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      const job = await slideBuilderJobsApi.get(jobId);
      if (job.status === 'completed') {
        if (!job.deck_id) throw new Error('Generate job completed without deck_id');
        return job;
      }
      if (job.status === 'failed') {
        throw new Error(job.error || job.message || 'Generate job failed');
      }
      if (Date.now() - started > timeoutMs) {
        throw new Error('Timed out waiting for slide generate job');
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  },
};
